import { lazy, Suspense, useEffect, useState, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react';
import { Spinner } from '../components/Status';
import { useI18n } from '../i18n';

/**
 * Parts of the app that are fetched when they are first wanted, not before.
 *
 * Everything used to arrive as one file: the tree, and with it the whole of
 * Admin controls, the recorder, the recipe studio, onboarding — well over a
 * megabyte that a phone had to download *and read through* before it could draw
 * the first circle. Most of the family will never open most of it. So the screen
 * you land on comes first, and the rest comes in pieces.
 *
 * Three things keep that from ever costing anybody anything:
 *
 *   - **they are fetched anyway, quietly, as soon as the app is idle**
 *     (`warmScreens`) — so by the time a second tab is pressed it is already
 *     there, and a phone that then loses its signal has the whole app;
 *   - **a piece that will not load is asked for again**, with a growing pause
 *     and at once when the connection comes back, rather than failing for good
 *     (a lazy component that has failed once stays failed for ever, which is a
 *     blank screen until the page is reloaded);
 *   - and while it is being fetched the screen says so, and after a few seconds
 *     says *why* it is taking so long, with a way to try again by hand.
 */
const loaders: Array<() => Promise<unknown>> = [];

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the same signature React's own `lazy` has: any component, its props kept
export function lazyScreen<T extends ComponentType<any>>(load: () => Promise<{ default: T }>): LazyExoticComponent<T> & { preload: () => void } {
  let asked: Promise<{ default: T }> | null = null;
  // One fetch however many callers — the warm-up and the first press may arrive together.
  const once = () => (asked ??= persistently(load));
  loaders.push(once);
  // `preload` starts the fetch without drawing anything, for a caller that knows this part is about to be wanted.
  return Object.assign(lazy(once), { preload: () => void once() });
}

/** Asks until it is answered: 1s, 2s, 4s, then every 8s — and straight away when the phone says it is back online. */
function persistently<T>(load: () => Promise<T>): Promise<T> {
  return new Promise<T>((resolve) => {
    let wait = 1000;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const attempt = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      load().then(
        (m) => {
          window.removeEventListener('online', attempt);
          resolve(m);
        },
        () => {
          timer = setTimeout(attempt, wait);
          wait = Math.min(8000, wait * 2);
        },
      );
    };
    window.addEventListener('online', attempt);
    attempt();
  });
}

/**
 * Fetch whatever has not been fetched yet, one piece at a time, once the app
 * has nothing better to do. Called once, after the first screen is up.
 */
export function warmScreens(): void {
  const idle: (fn: () => void) => void =
    typeof window.requestIdleCallback === 'function' ? (fn) => window.requestIdleCallback(fn, { timeout: 4000 }) : (fn) => setTimeout(fn, 1500);
  let i = 0;
  const next = () => {
    const load = loaders[i++];
    if (!load) return;
    // One after another: the point is to stay out of the way of whatever the person is doing.
    void load().then(() => idle(next));
  };
  idle(next);
}

/** What is shown while a piece arrives: a spinner, and — if it is taking long enough to wonder about — the reason and a way out. */
export function Arriving({ label }: { label?: string }) {
  const { t } = useI18n();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSlow(true), 5000);
    return () => clearTimeout(id);
  }, []);
  return (
    <>
      <Spinner label={label ?? t('common.loading')} />
      {slow && (
        <div className="empty">
          <p className="small muted">{t('error.network')}</p>
          <button type="button" className="btn btn-secondary mt-2" onClick={() => window.location.reload()}>
            {t('nav.tryAgain')}
          </button>
        </div>
      )}
    </>
  );
}

/**
 * A lazily fetched part, with its waiting state — so a call site reads like the
 * component it stands in for. `quiet` waits with nothing at all: for a part that
 * brings its own full-screen frame (the recipe studio), whose waiting state
 * would otherwise be a spinner dropped into the page underneath it.
 */
export function Lazily({ children, label, quiet }: { children: ReactNode; label?: string; quiet?: boolean }) {
  return <Suspense fallback={quiet ? null : <Arriving label={label} />}>{children}</Suspense>;
}
